const diagnosticRangeBackgroundPlugin = {
  id: "diagnosticRange",
  beforeDraw(chart, _args, options) {
    // beforeDraw runs before the chart draws its axes/tick labels, so the background is painted
    // first and the (mirrored) y-axis labels are drawn on top of it, instead of being covered by it.

    // If the bands, the y scale, or the chart area is not present, we don't want to draw anything.
    const bands = options?.bands;
    const { ctx, scales, chartArea } = chart;

    if (!bands?.length || !scales?.y || !chartArea) {
      return;
    }

    ctx.save();

    // For each band, we draw a rectangle the full canvas width so adjacent charts can meet flush
    // at a shared divider even when Chart.js reserves inner chart area for labels.
    for (const band of bands) {
      const top = pixelForValue(scales.y, band.to, chartArea.top);
      const bottom = pixelForValue(scales.y, band.from, chartArea.bottom);
      ctx.fillStyle = cssVariableColor(band.fill_token);
      ctx.fillRect(
        0,
        Math.min(top, bottom),
        chart.width,
        Math.abs(bottom - top)
      );
    }

    // For each band, we want to draw a darker line at the top of the band. We use the line_token to
    // determine the color of that line. We also use the y scale to convert the band.to value to
    // a pixel value, so that we can draw the line in the correct position. We also use the chart
    // area to get the left and right edges of the chart.
    ctx.lineWidth = 1;

    for (let index = 0; index < bands.length; index += 1) {
      const band = bands[index];
      drawBoundary(
        chart,
        band.from,
        lineTokenForBoundary(bands, index),
        chartArea.bottom,
      );
    }
    // lastBand is a special case because it doesn't have a band above it, so we want to draw the
    // line at the top of the band, not the bottom. We also want to use the line_token of the last
    // band to determine the color of that line.
    const lastBand = bands[bands.length - 1];
    drawBoundary(chart, lastBand.to, lastBand.line_token, chartArea.top);

    ctx.restore();
  },

  // afterDraw runs after the chart draws its axes/tick labels, so the labels are drawn first and the
  // boundary lines are drawn on top of them, instead of being covered by them.
  afterDraw(chart, _args, options) {
    if (options?.divider) {
      drawDivider(chart, options.divider);
    }

    if (!options?.bands?.length || !chart.scales?.y) {
      return;
    }

    const { ctx, scales, chartArea } = chart;
    const yScale = scales.y;

    ctx.save();

    // We want to draw the y-axis labels for each boundary, but we don't want them to overlap. So we
    // keep track of the previous label's y position and only draw the current label if it's at least
    // 4 pixels below the previous label.
    const boundaries = [...new Set(options.bands.flatMap(({ from, to }) => [from, to]))]
      .filter(Number.isFinite)
      .map((value) => ({ value, y: yScale.getPixelForValue(value) }))
      .sort((first, second) => first.y - second.y);
    const labelGap = 4; // minimum gap between labels in pixels, so they don't overlap.
    let previousLabelY = -Infinity;

    for (const boundary of boundaries) {
      if (boundary.y - previousLabelY < labelGap) {
        continue;
      }

      // We want to draw the label at the top of the boundary line, so we use the y position of the
      // boundary line to determine where to draw the label. We also want to draw the label above 
      // the line if it's not the bottom boundary, and below the line if it is the bottom boundary.
      const isBottomBoundary = boundary.y === chartArea.bottom;
      ctx.textBaseline = isBottomBoundary ? "top" : "bottom";
      ctx.fillText(
        yScale.getLabelForValue(boundary.value),
        6,
        boundary.y + (isBottomBoundary ? 3 : -3),
      );
      previousLabelY = boundary.y;
    }

    ctx.restore();
  },
};

// This function is used to determine which line_token to use for the boundary line between two
// bands. The first boundary uses the first bands line colour, the middle boundary uses the current
// bands line colour, and the last boundary uses the previous bands line colour.
// This is because the first boundary is the top of the first band, the middle boundaries are
// between two bands, and the last boundary is the bottom of the last band. The intent is to keep
// the boundary lines visually aligned with the bands they are associated with.
function lineTokenForBoundary(bands, boundaryIndex) {
  if (boundaryIndex === 0) {
    return bands[0].line_token;
  }

  const middle = (bands.length - 1) / 2;
  return boundaryIndex <= middle
    ? bands[boundaryIndex].line_token
    : bands[boundaryIndex - 1].line_token;
}

// This helper function is used to get the value of a CSS variable. It takes a token (the name of 
// the CSS variable) and returns the value of that variable. eg. if the token is "--color-success",
// it will return the value of that variable, which is green - hsl(134, 72%, 48%);.
function cssVariableColor(token) {
  return window.getComputedStyle(document.documentElement).getPropertyValue(token).trim();
}

// This helper function is used to get the pixel value for a given value on a scale. It takes a 
// scale, a value, and an edge (the pixel value to use if the value is null). It returns the pixel
// value for the given value on the scale. If the value is null, it returns the edge.
function pixelForValue(scale, value, edge) {
  return value === null ? edge : scale.getPixelForValue(value);
}

// Convert the boundary value to a pixel value using the y scale, and draw a line across the chart
// at that pixel value. The stroke colour is determined by the line_token, which comes from the 
// css variable associated with the band.
// nullEdge is the pixel value to use if the value is null.
function drawBoundary(chart, value, lineToken, nullEdge) {
  const { ctx, scales, chartArea } = chart;
  const y = pixelForValue(scales.y, value, nullEdge);

  ctx.strokeStyle = cssVariableColor(lineToken);
  ctx.beginPath();
  ctx.moveTo(0, y); // start at the left edge of the chart
  ctx.lineTo(chart.width, y); //  draw to the right edge of the chart
  ctx.stroke();
}

function drawDivider(chart, edge) {
  const { ctx, chartArea } = chart;
  const lineWidth = 2;
  const x = edge === "right" ? chart.width - lineWidth / 2 : lineWidth / 2;

  ctx.save();
  ctx.strokeStyle = "rgb(63 63 70)"; // zinc-700 — no CSS token, Tailwind only emits ones used in templates
  ctx.lineWidth = lineWidth;
  ctx.setLineDash([6, 4]);
  ctx.beginPath();
  ctx.moveTo(x, chartArea.top);
  ctx.lineTo(x, chartArea.bottom);
  ctx.stroke();
  ctx.restore();
}

export default diagnosticRangeBackgroundPlugin;
